import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { View, TouchableOpacity, FlatList } from "react-native";
import { useState } from "react";
import { VDBIcons } from "@utils/VDBIcons";
import { Button, Spacer, VDBText } from "@components/atoms";
import Modal from "@components/atoms/Modal";
import { SpaceType } from "@components/atoms/Spacer/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import RadioButton from "@components/atoms/RadioButton";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";

export type CustomSelectProps = {
  title: string;
  options: { label: string; value: string; isDisabled?: boolean }[];
  onSelect: (value: string) => void;
  defaultValue?: string;
  modalTitle?: string;
  screenText?: string;
};

const CustomSelectModal = ({
  options,
  defaultValue,
  onSelect,
  title,
  modalTitle,
  screenText,
}: CustomSelectProps) => {
  const { typography, theme } = useScreenOptionsHooks();
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [selectedLabel, setSelectedLabel] = useState(defaultValue);
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  const handleSelect = () => {
    setSelectedLabel(selectedValue);
    onSelect(selectedValue ?? "");
    setDropdownVisible(false);
  };

  const onClose = () => {
    if (selectedValue !== selectedLabel) {
      setSelectedValue(selectedLabel);
    }
    setDropdownVisible(false);
  };

  return (
    <Container>
      <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
        {t(screenText ?? "")}
      </VDBText>
      <Spacer size={SpaceType.Double} />
      <TouchableOpacity
        onPress={() => setDropdownVisible(true)}
        style={{
          height: "auto",
          padding: 10,
          borderRadius: 8,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          borderColor: theme["core-06"],
          borderWidth: 1,
          backgroundColor: theme["core-01"],
        }}
      >
        <View
          style={{
            flexDirection: "column",
            gap: 4,
            flexShrink: 1,
            flexGrow: 1,
          }}
        >
          {title && (
            <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
              {title}
            </VDBText>
          )}
          <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
            {options.find((option) => option.value === selectedLabel)?.label}
          </VDBText>
        </View>
        <VDBIcon
          size={24}
          color={theme["main-01"]}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.FILLED_CIRCLE_DOWN}
        />
      </TouchableOpacity>

      <Modal
        headerTitle={modalTitle || " "}
        isOpen={isDropdownVisible}
        onClose={onClose}
        showScrollbar={false}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: theme["core-01"],
            justifyContent: "center",
            alignItems: "center",
          }}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <ItemContainer>
            <FlatList
              data={options}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <RadioButton
                  label={item.label}
                  checked={item.value === selectedValue}
                  labelColor={
                    item.value === selectedValue
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                  onPress={() => setSelectedValue(item.value)}
                />
              )}
            />
            <Button
              type={ButtonType.primary}
              category={ButtonCategory.default}
              title={t("global.text.done")}
              onPress={handleSelect}
            />
          </ItemContainer>
        </TouchableOpacity>
      </Modal>
    </Container>
  );
};

export default CustomSelectModal;

const Container = styled.View``;

const ItemContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  width: 100%;
  max-width: 608px;
  min-height: 700px;
  padding-horizontal: ${SpaceType.ExtraSmall}px;
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isTablet &&
    css`
      margin-bottom: ${SpaceType.ExtraLarge}px;
      padding-top: ${SpaceType.Big}px;
    `};
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile &&
    css`
      padding: 16px;
      padding-bottom: 48px;
    `};
`;
